<template>
  <div :class="allClasses">
    <v-text-field
      v-if="byClassic"
      :value="internalDisplay"
      @input="val => changed(val)"
      @blur="blurred"
      @keydown="shortcutHandler"
      :rules="clarionDateValidation()"
      :label="label"
      class="ClarionDateInput"
      :id="dynamicId"
      :disabled="isDisabled"
      :readonly="isReadOnly"
      :dense="dense"
      :hide-details="hideDetails"
    >
      <template v-slot:prepend v-if="isToggle">
        <v-tooltip top right>
          <template v-slot:activator="{ on, attrs }">
            <v-icon x-small
              tabindex="-1"
              v-bind="attrs"
              v-on="on"
              @click="toggle"
            >{{calendarType}}</v-icon>
          </template>
          <span>Toggle between date controls</span>
        </v-tooltip>
      </template>
    </v-text-field>
    <v-text-field :id="dynamicId + '-vuetify-date-input'"
      v-if="!byClassic"
      :value="value"
      @input="val => changed(val)"
      @blur="blurred"
      type="date"
      class="VuetifyDateInput"
      :label="label"
      :disabled="isDisabled"
      :readonly="isReadOnly"
      :dense="dense"
      :hide-details="hideDetails"
    >
      <template v-slot:prepend>
        <v-tooltip top right>
          <template v-slot:activator="{ on, attrs }">
            <v-icon x-small
              v-bind="attrs"
              v-on="on"
              class="toggle"
              @click="toggle"
            >{{calendarType}}</v-icon>
          </template>
          <span>Toggle between date controls</span>
        </v-tooltip>
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  mdiCalendarCursor,
  mdiCalendarEdit,
} from '@mdi/js';

import {
  destructureDate,
  toIsoDate,
  toShortDate,
  stepPenaltyPeriodDate,
} from '../../util/shared/tmc-global';

export default {
  name: 'ClarionDateControl',
  components: {},
  data: () => ({
    byClassic: false,
    internalDisplay: '',
  }),
  props: {
    isToggle: {
      type: Boolean,
      default: true,
    },
    classes: {
      type: String,
      default: '',
    },
    value: [String, Number], // can handle null and undefined, eg. paid date
    isRequired: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    isIsoDate: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => ([]),
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    dynamicId: {
      type: String,
      default: 'defaultDynamicId',
    },
    // When set, up/down steps by a full penalty period instead of by a day:
    // jump to the next/previous month, landing on (penaltyChangeDay - 1) of
    // that month (clamped to the month's actual last day).
    penaltyChangeDay: {
      type: Number,
      default: null,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'dateControlClassic',
    ]),
    calendarType() {
      return this.byClassic ? mdiCalendarCursor : mdiCalendarEdit;
    },
    allClasses() {
      return [
        'ClarionDateControl',
        ...this.classes.split(','),
      ];
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        // When isIsoDate is true the parent stores YYYY-MM-DD, but users expect
        // MM-DD-YYYY in the text field. Convert the display value without
        // changing what is emitted — the v-model contract stays YYYY-MM-DD.
        if (this.isIsoDate && String(newVal || '').match(/^\d{4}-\d{2}-\d{2}$/)) {
          this.internalDisplay = toShortDate(newVal);
        } else {
          this.internalDisplay = newVal || '';
        }
      },
    },
  },
  created() {
    this.byClassic = this.dateControlClassic;
  },
  methods: {
    ...mapActions([
      'flashInfo',
    ]),
    toggle() {
      this.$emit('input', '');
      this.byClassic = !this.byClassic;
    },
    changed(newValue) {
      this.$emit('input', newValue);
    },
    blurred() {
      let dateStr;
      if (this.byClassic) {
        dateStr = this.clarionDateManipulation();
      } else {
        dateStr = this.value;
      }
      // This is a custom event for business logic hooks
      if ((dateStr || '').length > 0) {
        const { year, month, day } = destructureDate(dateStr);
        this.$emit('blurred', `${year}-${month}-${day}`);
      } else if (dateStr === '') this.$emit('blurred', null);
    },
    // pre-validation custom logic processing
    clarionDateManipulation() {
      // value.match(/([0-9]{4})[/\\-]{1}([0-9]{2})[/\\-]{1}([0-9]{2})/);
      const { year, month } = destructureDate(new Date());
      const { value } = this;
      if ((value || '').length === 0) {
        return '';
      }

      // console.info(`clarionDateManipulation: ${value}`);

      const isDayOfMonth = value.match(/^[./\\-]([0-9]+)$/);
      if (isDayOfMonth) {
        const newDateStr = `${month}-${isDayOfMonth[1]}-${year}`;
        return this.roundTripDate(newDateStr);
      }

      //  Special case when only single digit is entered show current fiscal year start date.
      const isBareD = value.match(/^[0-9]{1,2}$/);
      if (isBareD) {
        const newDateStr = `7-1-${year}`;
        return this.roundTripDate(newDateStr);
      }

      const isBareMonthDay = value.match(/^([0-9]{2})([0-9]{2})$/);
      if (isBareMonthDay) {
        const newDateStr = `${isBareMonthDay[1]}-${isBareMonthDay[2]}-${year}`;
        return this.roundTripDate(newDateStr);
      }

      const isBareMdy = value.match(/^([0-9]{2})([0-9]{2})([0-9]{2})$/);
      if (isBareMdy) {
        const newDateStr = `${isBareMdy[1]}-${isBareMdy[2]}-20${isBareMdy[3]}`;
        return this.roundTripDate(newDateStr);
      }

      const isBareFullMdy = value.match(/^([0-9]{2})([0-9]{2})([0-9]{4})$/);
      if (isBareFullMdy) {
        const checkYear = parseFloat(isBareFullMdy[3]);
        // note to future Y3K developer: sorry / not sorry :)
        if (checkYear > 1500 && checkYear < 3500) {
          const newDateStr = `${isBareFullMdy[1]}-${isBareFullMdy[2]}-${isBareFullMdy[3]}`;
          return this.roundTripDate(newDateStr);
        }
        const isBareFullYmd = value.match(/^([0-9]{4})([0-9]{2})([0-9]{2})$/);
        if (isBareFullYmd) {
          const newDateStr = `${isBareFullYmd[2]}-${isBareFullYmd[3]}-${isBareFullYmd[1]}`;
          return this.roundTripDate(newDateStr);
        }
        this.bail();
        return '';
      }

      const isBareMMDD = value.match(/^([0-9]{1,2})(\\|\.|\/|-)([0-9]{1,2})$/);
      //  to check regex for 08.23,8-23,08/23
      if (isBareMMDD) {
        const newDateStr = `${isBareMMDD[1]}-${isBareMMDD[3]}-${year}`;
        return this.roundTripDate(newDateStr);
      }

      const isBareMMDDYY = value.match(/^([0-9]{1,2})(\\|\.|\/|-)([0-9]{1,2})(\\|\.|\/|-)([0-9]{1,2})$/);
      if (isBareMMDDYY) {
        const newDateStr = `${isBareMMDDYY[1]}-${isBareMMDDYY[3]}-20${isBareMMDDYY[5]}`;
        return this.roundTripDate(newDateStr);
      }

      return this.roundTripDate();
    },
    // catch-all, standard JS formatting
    roundTripDate(newValueIfAny = undefined) {
      const value = newValueIfAny || this.value;
      // console.info(`round trip: ${newValueIfAny} vs ${this.value}`);
      let newDateStr;
      const { year, month, day } = destructureDate(value);
      if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
        this.bail();
        return '';
      }
      if (this.isIsoDate) {
        newDateStr = toIsoDate(value);
      } else {
        newDateStr = `${month}-${day}-${year}`;
      }
      this.$emit('input', newDateStr);
      return newDateStr;
    },
    bail() {
      this.$emit('input', '');
      const errMsg = `Unable to understand ${this.value || ''}`;
      this.$emit('error', errMsg);
      this.flashInfo(errMsg);
    },
    clarionDateValidation() {
      const internalRules = [
        (value) => {
          // console.log(`clarionDateValidation (reqd): ${value}`);
          const hasValue = !!value;
          if (!hasValue) {
            return (this.isRequired) ? 'Required' : true;
          }
          return true;
        },
        (value) => {
          // console.log(`clarionDateValidation (regex): ${value}`);
          if (this.isRequired || !!value) {
            const valueStr = (value || '').toString();
            const isMatch = valueStr.match(/^[0-9]+[/\\-][0-9]+[/\\-][0-9]+$/);
            if (!isMatch && valueStr.length > 0) {
              return 'Unexpected characters, or date is not in MM-DD-YYYY format.';
            }
          }
          return true;
        },
      ];
      return [
        ...internalRules,
        ...(this.rules || []),
      ];
    },
    shortcutHandler(e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // converting all the date formates to ISO date. Only ISO formats are working fine
        const conToIso = toIsoDate(this.value);
        if (this.penaltyChangeDay) {
          const direction = e.key === 'ArrowUp' ? 1 : -1;
          const { year, month, day } = stepPenaltyPeriodDate(
            conToIso, direction, this.penaltyChangeDay,
          );
          this.roundTripDate(`${month}-${day}-${year}`);
        } else {
          const upDownDate = new Date(conToIso);
          if (e.key === 'ArrowDown') {
            upDownDate.setUTCDate(upDownDate.getUTCDate() - 1);
          }
          if (e.key === 'ArrowUp') {
            upDownDate.setUTCDate(upDownDate.getUTCDate() + 1);
          }
          const { year, month, day } = destructureDate(upDownDate);
          this.roundTripDate(`${month}-${day}-${year}`);
        }
      }
      if (e.key === 'Enter') {
        this.blurred();
      }
      // for switching or focussing on tab
      if (e.key === 'Tab') this.$emit('emitOnTab', e);
    },
  },
};
</script>
